'use client';

import { useEffect, useState, useMemo } from 'react';
import Image from 'next/image';
import EmptyProviderLogo from '@/assets/png/empty-event-logo.png';

/**
 * SafeImage - A wrapper around Next.js Image component that handles errors gracefully
 *
 * Features:
 * - Validates URL format before attempting to load
 * - Automatically falls back to placeholder on image load error
 * - Handles missing, invalid, or malformed image URLs
 * - Automatically converts invalid sizes="auto" to proper responsive sizes
 * - Supports all Next.js Image props
 *
 * @param {object} props - All Next.js Image component props
 * @param {string} props.src - Image source URL
 * @param {string} props.fallbackSrc - Optional custom fallback image (defaults to empty-event-logo)
 * @param {string} props.sizes - Image sizes attribute (auto is converted to responsive sizes)
 * @param {function} props.onError - Optional custom error handler
 * @returns {JSX.Element} Image component with error handling
 */
export default function SafeImage({
  src,
  fallbackSrc = EmptyProviderLogo,
  onError,
  alt = "Image",
  sizes,
  ...props
}) {
  const [errorSrc, setErrorSrc] = useState(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setHasError(false);
    setErrorSrc(null);
  }, [src]);

  // Keep auto as a conservative fallback for legacy call sites until they get explicit sizes.
  const finalSizes = sizes === "auto"
    ? "(max-width: 940px) 100vw, (max-width: 1200px) 50vw, 400px"
    : sizes;

  /**
   * Validates if the provided URL is valid and safe to use
   * @param {string} url - The URL to validate
   * @returns {boolean} True if valid, false otherwise
   */
  const isValidImageUrl = (url) => {
    // Check for null, undefined, or empty string
    if (!url || url === '') {
      return false;
    }

    // Check if it's a static import (object with src property)
    if (typeof url === 'object' && url.src) {
      return true;
    }

    // Must be a string for further validation
    if (typeof url !== 'string') {
      return false;
    }

    // Trim whitespace
    const trimmedUrl = url.trim();
    if (trimmedUrl === '') {
      return false;
    }

    // Check for obviously malformed URLs
    if (trimmedUrl.includes(' ') || trimmedUrl.includes('\n') || trimmedUrl.includes('\t')) {
      console.warn(`Invalid image URL contains whitespace: ${trimmedUrl}`);
      return false;
    }

    // Allow relative paths (starting with /)
    if (trimmedUrl.startsWith('/')) {
      return true;
    }

    // Allow data URIs
    if (trimmedUrl.startsWith('data:')) {
      return true;
    }

    // Validate absolute URLs
    try {
      const urlObj = new URL(trimmedUrl);
      // Check for valid protocols
      const validProtocols = ['http:', 'https:'];
      if (!validProtocols.includes(urlObj.protocol)) {
        console.warn(`Invalid image URL protocol: ${urlObj.protocol} in ${trimmedUrl}`);
        return false;
      }
      return true;
    } catch (error) {
      console.warn(`Malformed image URL: ${trimmedUrl}`, error.message);
      return false;
    }
  };

  const handleError = (e) => {
    // Prevent infinite loop if fallback also fails
    if (!hasError) {
      console.warn(`Image failed to load: ${src}. Using fallback.`);
      setHasError(true);
      setErrorSrc(fallbackSrc);

      // Call custom error handler if provided
      if (onError) {
        onError(e);
      }
    }
  };

  // Validate and determine final source
  const finalSrc = useMemo(() => {
    // If src is missing, null, or undefined, use fallback immediately
    if (!src || src === '') {
      return fallbackSrc;
    }

    // If we already had an error, use the errorSrc (fallback)
    if (hasError) {
      return errorSrc;
    }

    // Validate the URL format
    if (!isValidImageUrl(src)) {
      console.warn(`Invalid image URL detected: ${src}. Using fallback.`);
      return fallbackSrc;
    }

    return src;
  }, [src, hasError, errorSrc, fallbackSrc]);

  return (
    <Image
      {...props}
      src={finalSrc}
      alt={alt}
      sizes={finalSizes}
      onError={handleError}
    />
  );
}
